import axios from "axios";
import { Machine } from "xstate";
import { assign, log } from "xstate/lib/actions";

import cannedDateRanges from "./canned-date-ranges";

// An XState state machine for managing the report parameters and
// loading the initial metadata(podcast, bounds). This is a terminal
// machine that finalizes when all the params and metadata are resolved.
// The parent machine starts a new instance when it want to change
// report parameters.

// TODO: we should probably validate the params

// TODO: we could return the podcast as soon as it is resolved so the page
// can start rendering more quickly.

const paramsMachine = Machine(
    {
        id: "params",
        initial: "preBounds",
        states: {
            // First we resolve the initial parameters that we need to load the
            // report bounds (which is the range of availble data for a podcast/report
            // across all time periods).
            preBounds: {
                type: "parallel",
                states: {
                    // Load the podcast based on the ID provided
                    podcast: {
                        initial: "pending",
                        states: {
                            pending: {
                                always: [
                                    { target: "loading", cond: "noPodcast" },
                                    "ready"
                                ]
                            },
                            loading: {
                                invoke: {
                                    src: "getPodcast",
                                    onDone: {
                                        target: "ready",
                                        actions: "setPodcast"
                                    },
                                    onError: "error"
                                },
                                exit: log(({ podcast }) => podcast, "loaded podcast")
                            },
                            ready: { type: "final" },
                            error: {
                                // raise() error or something?
                            }
                        }
                    },
                    // This is a little bit of overkill but gives as hook later to resolve
                    // the default report via an API call or a user pref or something.
                    report: {
                        initial: "pending",
                        states: {
                            pending: {
                                always: [{ target: "findDefault", cond: "noReport" }, "ready"]
                            },
                            findDefault: {
                                invoke: {
                                    src: "getDefaultReport",
                                    onDone: {
                                        target: "ready",
                                        actions: "setReport"
                                    },
                                    onError: "error"
                                },
                                exit: log(({ report }) => report, "default report")
                            },
                            ready: { type: "final" },
                            error: {
                                // raise() error or something?
                            }
                        }
                    },
                    // This is a little bit of overkill but gives as hook later to resolve
                    // the default period via an API call or a user pref or something.
                    period: {
                        initial: "pending",
                        states: {
                            pending: {
                                always: [{ target: "findDefault", cond: "noPeriod" }, "ready"]
                            },
                            findDefault: {
                                invoke: {
                                    src: "getDefaultPeriod",
                                    onDone: {
                                        target: "ready",
                                        actions: "setPeriod"
                                    },
                                    onError: "error"
                                },
                                exit: log(({ report }) => report, "default period")
                            },
                            ready: { type: "final" },
                            error: {
                                // raise() error or something?
                            }
                        }
                    }
                },
                onDone: "bounds"
            },
            // Once we have the podcast/report/period we can load the bounds
            // TODO: we really only need the podcastID here, and don't need
            // to wait for the full podcast to load. We could have parallel
            // states here for bounds/dates and podcast.
            bounds: {
                initial: "pending",
                states: {
                    pending: {
                        always: [{ target: "loading", cond: "noBounds" }, "ready"]
                    },
                    loading: {
                        invoke: {
                            src: "getBounds",
                            onDone: {
                                target: "ready",
                                actions: "setBounds"
                            },
                            onError: "error"
                        },
                        exit: log(({ bounds }) => bounds, "loaded bounds")
                    },
                    ready: { type: "final" },
                    error: {
                        // raise() error or something?
                    }
                },
                onDone: "dates"
            },
            // Once we have the bounds we can resolve the dates (using defaults when we don't have any).
            dates: {
                initial: "pending",
                states: {
                    pending: {
                        always: [{ target: "findDefault", cond: "noDates" }, { target: "ready" }]
                    },
                    findDefault: {
                        // reserve the possiblility that this is a user preference or something
                        invoke: {
                            src: "getDefaultDates",
                            onDone: {
                                target: "ready",
                                actions: "setDates"
                            },
                            onError: "error"
                        },
                        exit: log(
                            ({ reportStart, reportEnd }) => ({
                                reportStart,
                                reportEnd
                            }),
                            "default dates"
                        )
                    },
                    ready: { type: "final" },
                    error: {}
                },
                onDone: "ready"
            },
            ready: {
                type: "final",
                data: context => context
            }
        }
    },
    {
        actions: {
            setPodcastID: assign({ podcastID: (context, event) => event.data }),
            setPodcast: assign({
                podcast: (context, event) => event.data,
                feedFilter: (context, event) => event.data.feeds.map(p => p.podcastID)
            }),
            setReport: assign({ report: (context, event) => event.data }),
            setPeriod: assign({ period: (context, event) => event.data }),
            setBounds: assign({
                bounds: (context, event) => event.data,
                canned: (context, event) => cannedDateRanges(event.data)
            }),
            setDates: assign({
                reportStart: (context, event) => event.data.reportStart,
                reportEnd: (context, event) => event.data.reportEnd
            })
        },
        guards: {
            noPodcast: context => !context.podcast,
            noReport: context => !context.report,
            noPeriod: context => !context.period,
            noBounds: context => !context.bounds,
            noDates: context => !(context.reportStart && context.reportEnd)
        },
        services: {

            // Stand-ins for and API call/local storage for these other defaults
            getDefaultReport: (context, event) => Promise.resolve("episode"),
            getDefaultPeriod: (context, event) => Promise.resolve("week"),

            // If no dates use the "preferred" date range for the selected time period
            getDefaultDates: ({ canned, period }, event) =>
                Promise.resolve({
                    reportStart: canned[period].preferred.start,
                    reportEnd: canned[period].preferred.end
                }),

            // API methods
            getPodcast: ({ accountID, podcastID }, event) =>
                axios({
                    url: `/api/${accountID}/measurement/podcasts/${podcastID}`
                }).then(({ data }) => data),
            getBounds: ({ accountID, podcastID, report }, event) =>
                axios({
                    url: `/api/${accountID}/measurement/reports/${podcastID}/${report}/bounds`
                }).then(({ data }) => data)
        }
    }
);

export default paramsMachine;
